import React from 'react';
import Sheet from 'ui/Sheet/Sheet';
import {
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetFooter,
  KompisSheetFooterButtons,
  KompisSheetHeader
} from '@inter-ikea-kompis/react-components';
import styled from 'styled-components';
import media from 'ui/media';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Title from 'ui/Typography/Title';
import { getSheetProps, setQuestionSheetVisible } from 'ducks/questionSheet';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useAppDispatch } from 'ducks/hooks';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { MenuType } from 'analytics/interaction';
import { getSelectedGroupId } from 'ducks/products';
import { useBehaviorTracker } from 'contexts/behaviors';

const TitleWrapper = styled.div`
  ${media.mobile`
    width: 100%;
    padding: 0.5rem 0;
  `}
`;

interface ImageProps {
  paddingHorizontal: number;
}
const Image = styled.img<ImageProps>`
  width: 100%;
  padding: 0 ${({ paddingHorizontal }) => paddingHorizontal}px;
`;

const QuestionSheet = () => {
  const dispatch = useAppDispatch();
  const behaviorTracker = useBehaviorTracker();
  const selectedGroupId = useSelector(getSelectedGroupId);
  const { formatMessage } = useIntl();
  const {
    title,
    preamble,
    description,
    afterImageDescription,
    imageHorizontalPadding,
    primaryButtonText,
    secondaryButtonText,
    onAccept,
    onDismiss,
    modalVisible,
    imageRealtivePath,
    imageAltText
  } = useSelector(getSheetProps);

  const onClose = () => {
    dispatch(setQuestionSheetVisible(false));
  };

  const sheetContext = {
    groupId: selectedGroupId,
    menuType: 'sheet' as MenuType,
    widgetId: 'question-sheet'
  };

  return (
    <Sheet
      data-testid="questions-handles-sheet"
      visible={!!modalVisible}
      onOverlayClick={onClose}
      zIndex={501} // z-index needs to be greater than 500 to show on top of other sheets
    >
      <KompisSheetHeader theme={SkapaTheme} onCloseButtonClick={onClose} heading="" />
      <KompisSheetBody theme={SkapaTheme}>
        <KompisSheetBodyPadding theme={SkapaTheme}>
          {title && (
            <TitleWrapper>
              <Title pb="s" type={ThemeFontStyleTypeEnum.headingMedium}>
                {formatMessage({ id: title })}
              </Title>
            </TitleWrapper>
          )}
          {preamble && (
            <Body py="md" mb={description ? 0 : 'l'} type={ThemeFontStyleTypeEnum.bodyMedium}>
              {formatMessage({ id: preamble })}
            </Body>
          )}
          {description && (
            <Body py="md" mb="l" type={ThemeFontStyleTypeEnum.bodyMedium}>
              {formatMessage({ id: description })}
            </Body>
          )}
          <Image
            src={imageRealtivePath}
            alt={imageAltText && formatMessage({ id: imageAltText })}
            paddingHorizontal={imageHorizontalPadding ?? 0}
          />
          {afterImageDescription && (
            <Body py="md" mt="l" type={ThemeFontStyleTypeEnum.bodyMedium}>
              {formatMessage({ id: afterImageDescription.text }, afterImageDescription.props)}
            </Body>
          )}
        </KompisSheetBodyPadding>
      </KompisSheetBody>
      <KompisSheetFooter theme={SkapaTheme}>
        <KompisSheetFooterButtons
          compact={false}
          data-testid="question-sheet-footer-buttons"
          theme={SkapaTheme}
          primaryLabel={primaryButtonText && formatMessage({ id: primaryButtonText })}
          secondaryLabel={secondaryButtonText && formatMessage({ id: secondaryButtonText })}
          onButtonClick={event => {
            const isAccept = event.detail.type === 'primaryButton';
            behaviorTracker.withContext(sheetContext).custom(() => {
              if (isAccept) {
                onAccept && onAccept();
              } else {
                onDismiss && onDismiss();
              }
            });
          }}
        />
      </KompisSheetFooter>
    </Sheet>
  );
};

export default QuestionSheet;
